import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublicTextShare } from "../../utils/Api";
import PublicNavbar from "../../components/PublicNavbar";
import Footer from "../../components/Footer";

interface SharedBlock {
  token: string;
  document_name: string;
  owner_name: string;
  block_uuid: string;
  text: string;
  created_at: string;
  expires_at: string;
}

function ExpiryBar({ createdAt, expiresAt }: { createdAt: string; expiresAt: string }) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const update = () => {
      const start = new Date(createdAt).getTime();
      const end = new Date(expiresAt).getTime();
      const now = Date.now();
      const elapsed = Math.max(0, now - start);
      const total = end - start;
      setPct(Math.min(100, (elapsed / total) * 100));
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [createdAt, expiresAt]);

  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-gray-400">Link expiry</span>
        <span className="text-xs text-gray-500 font-medium">
          {daysLeft === 0 ? "Expires today" : `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`}
        </span>
      </div>
      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 bg-[#1d365d]"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

const ShareView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [block, setBlock] = useState<SharedBlock | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"not_found" | "expired" | null>(null);

  useEffect(() => {
    if (!token) return;
    getPublicTextShare(token)
      .then((res) => setBlock(res.data))
      .catch((err) => {
        setError(err?.response?.status === 410 ? "expired" : "not_found");
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <>
        <PublicNavbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !block) {
    return (
      <>
        <PublicNavbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-700">
              {error === "expired" ? "Link expired" : "Link not found"}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {error === "expired"
                ? "This share link has expired. Ask the sender to generate a new one."
                : "This share link may have been removed."}
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <PublicNavbar />
      <div className="min-h-screen bg-gray-50 flex flex-col items-center py-16 px-4">
        <div className="w-full max-w-2xl">
          <div className="mb-5">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Shared from</p>
            <h1 className="text-xl font-semibold text-gray-800">{block.document_name}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              by <span className="font-medium text-gray-700">{block.owner_name}</span>
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-6 py-5">
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{block.text}</p>
          </div>

          <ExpiryBar createdAt={block.created_at} expiresAt={block.expires_at} />

          <p className="mt-6 text-center text-xs text-gray-400">
            Shared via{" "}
            <a href="/" className="text-blue-500 hover:underline">
              Data Revival
            </a>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ShareView;
